import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../stores/authStore';
import useFocusOnMount from '../hooks/useFocusOnMount';
import styles from './AuthPages.module.css';

export default function LoginPage() {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const { login, isLoading, error, clearError } = useAuthStore();
  const navigate = useNavigate();
  const mainRef = useFocusOnMount();

  async function handleSubmit(e) {
    e.preventDefault();
    clearError();
    try {
      await login({ email, password });
      navigate('/');
    } catch {
      // Error is set in store
    }
  }

  return (
    <main className={styles.page} ref={mainRef} tabIndex={-1}>
      {/* Left panel — illustration + logo */}
      <div className={styles.authLeft}>
        <img
          src="/assets/auth-illustration.webp"
          alt=""
          className={styles.bgImage}
        />
        <img
          src="/assets/wanderwords-logo.svg"
          alt="WanderWords"
          className={styles.logo}
        />
      </div>

      {/* Right panel — form + footer */}
      <div className={styles.authRight}>
        <div className={styles.content}>
        {error && (
          <div className={styles.errorBanner} role="alert">{error}</div>
        )}

        <h1 className={styles.cardTitle}>{t('auth.login')}</h1>

        <form id="login-form" onSubmit={handleSubmit} className={styles.form}>
          <div className={styles.fieldGroup}>
            <label htmlFor="login-email" className={styles.label}>{t('auth.email')}</label>
            <input
              id="login-email"
              type="email"
              placeholder={t('auth.emailPlaceholder')}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className={styles.input}
            />
          </div>

          <div className={styles.fieldGroup}>
            <label htmlFor="login-password" className={styles.label}>{t('auth.password')}</label>
            <input
              id="login-password"
              type="password"
              placeholder={t('auth.passwordHint')}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={8}
              className={styles.input}
            />
          </div>
        </form>

        <div className={styles.actions}>
          <button
            type="submit"
            form="login-form"
            disabled={isLoading}
            className={styles.submitBtn}
          >
            {isLoading ? t('auth.signingIn') : t('auth.login')}
          </button>

          <p className={styles.switchPrompt}>
            {t('auth.noAccount')}{' '}
            <Link to="/signup" className={styles.switchLink}>
              {t('auth.signUp')}
            </Link>
          </p>

          <button
            type="button"
            className={styles.forgotPassword}
            onClick={() => navigate('/forgot-password')}
          >
            {t('auth.forgotPassword')}
          </button>
        </div>
      </div>

      {/* Page footer */}
      <footer className={styles.pageFooter}>
        <a
          href="https://wanderwords.co/terms"
          target="_blank"
          rel="noopener noreferrer"
          className={styles.footerLink}
        >
          Terms of Service
        </a>
        <a
          href="https://wanderwords.co/privacy"
          target="_blank"
          rel="noopener noreferrer"
          className={styles.footerLink}
        >
          Privacy Policy
        </a>
        <p className={styles.footerCopyright}>&copy; 2026 Bricksquare Pte. Ltd.</p>
        </footer>
      </div>
    </main>
  );
}
